import { Tag } from 'antd';
import { ICellRendererProps } from 'common/components/table/table.types';
import { ICitationRead } from 'common/services/citation-service/citation.service.types';
import dayjs from 'dayjs';
import {
  CitationStatus,
  citationStatusColorMap,
  citationStatusMap,
  getCitationStatusOptions,
} from 'features/eiq-manage/enums/citation-status.enum';
import { CitationImages } from './citation-images/citation-images';
import { IFilterConfig } from 'common/components/table/filters/column-filter/column-filter.types';
import { FilterTypesEnum } from 'common/enums/filter-types.enum';
import { StrToDateTimeLocal } from 'common/helpers/date.helper';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { visitorTypeMap, visitorTypeMapColorMap, visitorTypeOptions } from 'common/constans/visitor.constants';
import { TrafficLogVisitorType } from 'common/constans/common.constants';

dayjs.extend(isSameOrBefore);

export const citationsInitialColumns = [
  {
    key: 'externalId',
    title: 'Citation #',
    dataKey: 'externalId',
    width: 120,
    fixed: 1,
    resizable: true,
    sortable: true,
  },
  {
    key: 'occurredAt',
    title: 'Date',
    dataKey: 'occurredAt',
    width: 190,
    fixed: 1,
    resizable: true,
    sortable: true,
    cellRenderer: (cell: any) => {
      return cell.cellData && <span>{StrToDateTimeLocal(cell.cellData)?.format('MMM D, YYYY hh:mm:ss a')}</span>;
    },
  },
  {
    key: 'photosUrl',
    title: 'Photos',
    dataKey: 'photosUrl',
    width: 180,
    fixed: 1,
    resizable: false,
    sortable: false,
    cellRenderer: (cell: ICellRendererProps<ICitationRead>) => {
      return cell.cellData && <CitationImages photosUrl={[...cell.rowData.photosUrl, ...cell.rowData.licensePlatePhotosUrl]} />;
    },
  },
  {
    key: 'ownerType',
    title: 'Type',
    dataKey: 'ownerType',
    width: 180,
    fixed: 1,
    resizable: false,
    sortable: false,
    cellRenderer: (cell: ICellRendererProps<ICitationRead>) => {
      return (
        <Tag className="eiq-tag" color={visitorTypeMapColorMap[cell?.cellData as TrafficLogVisitorType]} style={{ color: '#292B2E' }}>
          {visitorTypeMap[cell?.cellData as TrafficLogVisitorType]}
        </Tag>
      );
    },
  },
  {
    key: 'residentName',
    title: 'Resident Name',
    dataKey: 'residentName',
    width: 160,
    fixed: 1,
    resizable: true,
    sortable: true,
  },
  {
    key: 'guestName',
    title: 'Guest Name',
    dataKey: 'guestName',
    width: 160,
    fixed: 1,
    resizable: true,
    sortable: true,
  },
  {
    key: 'companyName',
    title: 'Company',
    dataKey: 'companyName',
    width: 160,
    fixed: 1,
    resizable: true,
    sortable: true,
  },
  {
    key: 'licensePlate',
    title: 'License Plate',
    dataKey: 'licensePlate',
    width: 150,
    fixed: 1,
    resizable: true,
    sortable: true,
  },
  {
    key: 'licenseState',
    title: 'License State',
    dataKey: 'licenseState',
    width: 150,
    fixed: 1,
    resizable: true,
    sortable: true,
  },
  {
    key: 'sequenceNumber',
    title: 'Citation Count',
    dataKey: 'sequenceNumber',
    width: 150,
    fixed: 1,
    resizable: true,
    sortable: true,
    cellRenderer: (cell: ICellRendererProps<ICitationRead>) => {
      return cell.cellData ? cell.cellData : null;
    },
  },
  {
    key: 'status',
    title: 'Status',
    dataKey: 'status',
    width: 120,
    fixed: 1,
    resizable: true,
    sortable: true,
    cellRenderer: (cell: ICellRendererProps<ICitationRead>) => {
      return (
        <Tag className="eiq-tag" color={citationStatusColorMap[cell?.rowData?.status as CitationStatus]} style={{ color: '#292B2E' }}>
          {citationStatusMap[cell?.rowData?.status as CitationStatus]}
        </Tag>
      );
    },
  },
  {
    key: 'speedLimit',
    title: 'Speed Limit',
    dataKey: 'speedLimit',
    width: 180,
    fixed: 1,
    resizable: true,
    sortable: true,
    cellRenderer: (cell: ICellRendererProps<ICitationRead>) => {
      return cell.cellData && `${cell.cellData} mph`;
    },
  },
  {
    key: 'violatingSpeed',
    title: 'Actual Speed',
    dataKey: 'violatingSpeed',
    width: 180,
    minWidth: 180,
    resizable: true,
    sortable: true,
    flexGrow: 1,
    cellRenderer: (cell: ICellRendererProps<ICitationRead>) => {
      return cell.cellData && `${cell.cellData} mph`;
    },
  },
  {
    key: 'totalPaymentAmount',
    title: 'Fine Amount',
    dataKey: 'totalPaymentAmount',
    width: 170,
    fixed: 1,
    resizable: true,
    sortable: false,
    cellRenderer: (cell: ICellRendererProps<ICitationRead>) => {
      return `$${cell?.rowData?.totalPaymentAmount}`;
    },
  },
  {
    key: 'commissionAmount',
    title: 'Fees',
    dataKey: 'commissionAmount',
    width: 170,
    fixed: 1,
    resizable: true,
    sortable: false,
    cellRenderer: (cell: ICellRendererProps<ICitationRead>) => {
      return `$${cell?.rowData?.commissionAmount}`;
    },
  },
  {
    key: 'communityPaymentAmount',
    title: 'HOA Receives',
    dataKey: 'communityPaymentAmount',
    width: 150,
    fixed: 1,
    resizable: true,
    sortable: false,
    cellRenderer: (cell: ICellRendererProps<ICitationRead>) => {
      return `$${cell?.rowData?.communityPaymentAmount}`;
    },
  },
  {
    key: 'currentSuspensionDays',
    title: 'Current Suspension (days)',
    dataKey: 'currentSuspensionDays',
    width: 230,
    fixed: 1,
    resizable: true,
    sortable: false,
  },
  {
    key: 'totalSuspensionDays',
    title: 'Citation Suspension (days)',
    dataKey: 'totalSuspensionDays',
    width: 230,
    fixed: 1,
    resizable: true,
    sortable: false,
  },
  {
    key: 'currentRevokeDrivingPrivilegesDays',
    title: 'Current Revoke Driving Privileges (days)',
    dataKey: 'currentRevokeDrivingPrivilegesDays',
    width: 290,
    fixed: 1,
    resizable: true,
    sortable: false,
  },
  {
    key: 'totalRevokeDrivingPrivilegesDays',
    title: 'Revoke Driving Privileges (days)',
    dataKey: 'totalRevokeDrivingPrivilegesDays',
    width: 290,
    fixed: 1,
    resizable: true,
    sortable: false,
  },
  {
    key: 'cameraName',
    title: 'Camera Name',
    dataKey: 'cameraName',
    width: 230,
    fixed: 1,
    resizable: true,
    sortable: false,
    hidden: true,
  },
  {
    key: 'locationName',
    title: 'Location Name',
    dataKey: 'locationName',
    width: 230,
    fixed: 1,
    resizable: true,
    sortable: false,
    hidden: true,
  },
];

export const citationOwnerTypeOptions = [{ label: 'Unidentified', value: 0 }, ...visitorTypeOptions];

export const citationFilterConfig: IFilterConfig[] = [
  {
    name: 'status',
    filterType: FilterTypesEnum.Equals,
    label: 'Status',
    type: {
      options: getCitationStatusOptions(),
      type: 'SELECT',
      mode: 'multiple',
    },
  },
  {
    name: 'ownerType',
    filterType: FilterTypesEnum.Equals,
    label: 'Type',
    type: {
      options: citationOwnerTypeOptions,
      type: 'SELECT',
      mode: 'multiple',
    },
  },
  {
    name: 'occurredAt',
    filterType: FilterTypesEnum.Equals,
    label: 'Dates',
    type: {
      nameEnd: 'occurredAt',
      type: 'DATE_RANGE',
    },
    onChange(filterValue, tableContext) {
      if (filterValue) {
        tableContext.updateFilter(
          'occurredAt',
          [
            { operator: FilterTypesEnum.GreaterThanOrEqual, value: filterValue.start, innerFilterType: 'AND' },
            { operator: FilterTypesEnum.LessThan, value: filterValue.end },
          ],
          'AND',
        );
      }
    },
  },
  {
    name: 'sequenceNumber',
    filterType: FilterTypesEnum.Equals,
    label: 'Citation Count',
    type: {
      nameEnd: 'sequenceNumber',
      type: 'NUMBER_RANGE',
    },
    onChange(filterValue, tableContext) {
      if (filterValue) {
        tableContext.updateFilter(
          'sequenceNumber',
          [
            { operator: FilterTypesEnum.GreaterThanOrEqual, value: filterValue.start, innerFilterType: 'AND' },
            { operator: FilterTypesEnum.LessThanOrEqual, value: filterValue.end },
          ],
          'AND',
        );
      }
    },
  },
];
